import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Quote, User, Users } from 'lucide-react';
import { GoodThing } from '../../types/good-things';
import { Skeleton } from '../ui/skeleton';
import { formatDateET } from '../../utils/dateUtils';
import { randomGoodThingService } from '../../services/randomGoodThingService';

export const RandomGoodThing: React.FC = () => {
  const [goodThing, setGoodThing] = useState<GoodThing | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRandomGoodThing = async () => {
      try {
        const randomGoodThing = await randomGoodThingService.getRandomGoodThing();
        setGoodThing(randomGoodThing);
      } catch (error) {
        console.error('Error fetching random good thing:', error);
        // Silently fail - no error state
      } finally {
        setIsLoading(false);
      }
    };

    fetchRandomGoodThing();
    // No cleanup or retry mechanism
  }, []);

  const formatName = (email: string): string => {
    return email
      .split('@')[0]
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const formatRecipients = (recipients: string[]): string => {
    if (recipients.length === 0) return '';
    if (recipients.length === 1) return formatName(recipients[0]);
    
    // Format all recipients with commas and "and" before the last one
    const formattedNames = recipients.map(recipient => formatName(recipient));
    
    if (recipients.length === 2) {
      return `${formattedNames[0]} and ${formattedNames[1]}`;
    }
    
    // For 3 or more recipients, use commas and "and" before the last one
    const lastRecipient = formattedNames.pop();
    return `${formattedNames.join(', ')} and ${lastRecipient}`;
  };

  if (isLoading) {
    return (
      <Card className="mb-8">
        <CardContent className="pt-6">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  // If no good thing found, return null (don't render anything)
  if (!goodThing) {
    return null;
  }

  return (
    <Card className="mb-8 bg-gradient-to-r from-pink-50 to-white dark:from-pink-950 dark:to-gray-900 overflow-hidden relative">
      <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-16 -translate-y-8">
        <div className="absolute inset-0 bg-pink-100 dark:bg-pink-900 rounded-full opacity-50"></div>
      </div>
      <CardContent className="pt-6 relative">
        <div className="flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <Quote className="h-8 w-8 text-pink-400 dark:text-pink-300 flex-shrink-0" />
            <h3 className="font-medium text-gray-900 dark:text-white">Random Good Thing of the Week</h3>
          </div>
          
          <div className="flex flex-wrap items-start gap-4 mb-4 text-sm">
            {goodThing.recipients && goodThing.recipients.length > 0 && (
              <div className="flex items-center">
                <span className="font-medium text-blue-600 dark:text-blue-400 mr-1">To:</span>
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-1" />
                <span className="text-gray-800 dark:text-gray-200">{formatRecipients(goodThing.recipients)}</span>
              </div>
            )}
            
            <div className="flex items-center">
              <span className="font-medium text-green-600 dark:text-green-400 mr-1">From:</span>
              <User className="h-4 w-4 text-green-600 dark:text-green-400 mr-1" />
              <span className="text-gray-800 dark:text-gray-200">{formatName(goodThing.submitter)}</span>
            </div>
          </div>
          
          <div>
            <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm md:text-lg leading-relaxed">{goodThing.note}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RandomGoodThing;
